import { useEffect, useMemo, useRef, useState } from 'react'
import {
  computeTotp,
  computeTotpWithKey,
  decodeBase32,
  importHmacSha1Key,
  type TotpOptions,
  type TotpResult,
} from '../services/totp'

/**
 * Base32 TOTP secret から live rotating OTP を返すフック (T-D-Vault-TOTP)。
 *
 * 設計判断:
 *   - **Web Crypto 経由 + async**: 内部の `computeTotp` は `crypto.subtle.sign`
 *     を呼ぶため async。tick ごとに非同期計算 → useState で結果を保持し
 *     再描画する。stale な前 tick の解決が遅れて新 tick の結果を上書きしないよう、
 *     最新 tick の id を ref で持って discard する。
 *   - **再描画レート**: 1 秒に 1 回 (`setInterval(1000)`)。30 秒周期内でも
 *     progress bar / remaining seconds を滑らかに更新するため、tick ごとに
 *     React state を更新する。code 自体は周期が変わったときだけ変わる。
 *   - **decoded bytes の memoize**: `useMemo([base32Secret])` で
 *     `decodeBase32` 結果をキャッシュし、tick ごとの base32 再パースを
 *     避ける。invalid な base32 は `error` 状態として返却し、コンポーネントが
 *     fallback 表示する責務。
 *   - **DI**: `nowProvider` / `setInterval` / `clearInterval` を options で
 *     上書き可能にし、spec で fake time を使えるようにする。既定は
 *     `Date.now` + `globalThis.setInterval`。
 */

export interface UseTotpOptions extends TotpOptions {
  /** 描画更新間隔 (ms)。既定 1000 = 1 秒。spec では小さくしてテストを高速化可能。 */
  refreshIntervalMs?: number

  /** テスト用 DI: 現在時刻。 */
  nowProvider?: () => number

  /** テスト用 DI: timer 起動。 */
  setIntervalImpl?: (fn: () => void, ms: number) => ReturnType<typeof globalThis.setInterval>

  /** テスト用 DI: timer 取消。 */
  clearIntervalImpl?: (id: ReturnType<typeof globalThis.setInterval>) => void
}

export type UseTotpState =
  | { kind: 'disabled' }
  | { kind: 'computing' }
  | { kind: 'error'; messageKey: string }
  | {
      kind: 'active'
      code: string
      periodMs: number
      remainingMs: number
      /** 0..1 の経過比率 (progress bar 用)。0 が周期開始、1 が満了直前。 */
      progress: number
    }

export const useTotp = (
  /** Base32 形式の secret。`undefined` / 空文字 / 空白だけの場合は disabled。 */
  base32Secret: string | undefined,
  options: UseTotpOptions = {},
): UseTotpState => {
  const refreshMs = options.refreshIntervalMs ?? 1000

  // 不変参照に固める (effect の依存に入れない)
  const nowRef = useRef(options.nowProvider ?? Date.now)

  nowRef.current = options.nowProvider ?? Date.now

  const setIntervalRef = useRef(options.setIntervalImpl ?? globalThis.setInterval.bind(globalThis))

  setIntervalRef.current = options.setIntervalImpl ?? globalThis.setInterval.bind(globalThis)

  const clearIntervalRef = useRef(
    options.clearIntervalImpl ?? globalThis.clearInterval.bind(globalThis),
  )

  clearIntervalRef.current = options.clearIntervalImpl ?? globalThis.clearInterval.bind(globalThis)

  // decoded bytes (memoized) + decode error
  const decoded = useMemo(() => {
    const trimmed = (base32Secret ?? '').trim()
    if (trimmed.length === 0) {
      return { kind: 'disabled' as const }
    }
    try {
      return { kind: 'ok' as const, bytes: decodeBase32(trimmed) }
    } catch {
      return { kind: 'error' as const, messageKey: 'vault.detail.totp.error.invalid_secret' }
    }
  }, [base32Secret])

  // 非同期計算結果を保持する state。tick ごとに上書きされる。
  const [result, setResult] = useState<TotpResult | null>(null)
  // 最新 tick 番号。古い tick の Promise が遅れて解決しても discard するためのガード。
  const latestTickRef = useRef(0)
  // decoded.bytes が変わったときだけ importKey し直す (1 秒ごとの importKey を回避)。
  const cryptoKeyRef = useRef<CryptoKey | null>(null)
  const optionsPeriod = options.periodSec
  const optionsDigits = options.digits

  // CryptoKey の事前インポート — decoded.bytes が変わった時のみ再実行。
  useEffect(() => {
    if (decoded.kind !== 'ok') {
      cryptoKeyRef.current = null

      return
    }

    let mounted = true

    void importHmacSha1Key(decoded.bytes).then(key => {
      if (mounted) cryptoKeyRef.current = key
    })

    return () => {
      mounted = false
    }
  }, [decoded])

  useEffect(() => {
    if (decoded.kind !== 'ok') {
      setResult(null)

      return
    }

    let mounted = true

    const fire = (): void => {
      latestTickRef.current += 1

      const myTick = latestTickRef.current

      // キャッシュ済み CryptoKey があれば importKey を呼ばない経路で計算する。
      const key = cryptoKeyRef.current

      const computePromise = key
        ? computeTotpWithKey(key, nowRef.current(), {
            periodSec: optionsPeriod,
            digits: optionsDigits,
          })
        : computeTotp(decoded.bytes, nowRef.current(), {
            periodSec: optionsPeriod,
            digits: optionsDigits,
          })

      void computePromise.then(r => {
        if (!mounted) return
        if (myTick !== latestTickRef.current) return
        setResult(r)
      })
    }

    fire() // 起動直後の即時計算

    const id = setIntervalRef.current(fire, refreshMs)

    return () => {
      mounted = false
      clearIntervalRef.current(id)
    }
  }, [decoded, refreshMs, optionsPeriod, optionsDigits])

  return useMemo<UseTotpState>(() => {
    if (decoded.kind === 'disabled') return { kind: 'disabled' }
    if (decoded.kind === 'error') return { kind: 'error', messageKey: decoded.messageKey }
    if (result === null) return { kind: 'computing' }
    const progress = result.elapsedMs / result.periodMs

    return {
      kind: 'active',
      code: result.code,
      periodMs: result.periodMs,
      remainingMs: result.remainingMs,
      progress,
    }
  }, [decoded, result])
}
